// Min-wall resolution and outcome logic for the UTT department.
//
// Model: a scan plan covers an equipment type (asset code, unique 99% of the
// time). Because assets sharing a code can be built by different OEMs with
// different minimums, values live on plan VARIANTS (one per OEM/spec):
//   variants[variantId].minWalls[locationId] -> { value, source, ... }
// An asset (GPS#) resolves to a variant via:
//   1. overrides[gps][locationId]  — per-asset derate, beats everything
//   2. assetAssignments[gps]       — explicit variant pin (bulk-assigned)
//   3. OEM match                   — asset's manufacturer vs variant.oem
//   4. sole variant                — plans with one variant need no mapping
// Cell source is either an OEM document citation ("oem": oemDocId/page/note)
// or an engineering calculation ("calc": material spec, dimensions, formula).

// Find the scan plans applicable to an asset code.
export function plansForAssetCode(scanPlans, assetCode) {
  if (!assetCode) return [];
  return Object.entries(scanPlans || {})
    .filter(
      ([, plan]) =>
        !plan.deleted &&
        plan.status !== "retired" &&
        (plan.assetCodes || []).includes(assetCode)
    )
    .map(([id, plan]) => ({ id, ...plan }));
}

// Resolve which variant applies to an asset. Returns a variantId or null
// (null with multiple variants means the UI must ask).
export function resolveVariantId(plan, assetNumber, assetOem) {
  if (!plan) return null;
  const assigned = assetNumber && plan.assetAssignments?.[assetNumber];
  if (assigned && plan.variants?.[assigned]) return assigned;
  const ids = Object.keys(plan.variants || {});
  if (assetOem) {
    const match = ids.find(
      (id) =>
        (plan.variants[id].oem || "").trim().toLowerCase() ===
        assetOem.trim().toLowerCase()
    );
    if (match) return match;
  }
  if (ids.length === 1) return ids[0];
  return null;
}

// Effective minimum for one location: per-asset override first, then the
// resolved variant's value.
export function resolveMinWall(plan, variantId, assetNumber, locationId) {
  const override = assetNumber && plan?.overrides?.[assetNumber]?.[locationId];
  if (override && override.value !== undefined && override.value !== null) {
    return { ...override, minSource: "override" };
  }
  const cell = variantId && plan?.variants?.[variantId]?.minWalls?.[locationId];
  if (cell && cell.value !== undefined && cell.value !== null) {
    return { ...cell, minSource: "variant" };
  }
  return null;
}

// Readings scaffold for a plan+variant: one row per location with its
// resolved minimum snapshotted in (audit trail — later reference edits must
// not change what a past reading was judged against).
export function buildPlanReadings(plan, variantId, assetNumber) {
  return (plan?.locations || []).map((loc) => {
    const min = resolveMinWall(plan, variantId, assetNumber, loc.id);
    return {
      locationId: loc.id,
      label: loc.label,
      value: "",
      minWall: min ? Number(min.value) : null,
      oemDocId: min ? min.oemDocId || null : null,
      source: min ? min.source || null : null,
      minSource: min ? min.minSource : null,
    };
  });
}

export function readingResult(reading) {
  const value = Number(reading.value);
  if (reading.value === "" || reading.value === null || Number.isNaN(value)) {
    return null;
  }
  if (reading.minWall === null || reading.minWall === undefined) return null;
  return value >= Number(reading.minWall) ? "pass" : "fail";
}

// Roll a set of readings + the visual check into the entry outcome.
// Readiness contract: outcome is one of 'pass' | 'fail'.
export function computeOutcome(readings, visual) {
  if (visual === false) return "fail";
  const results = (readings || []).map(readingResult);
  if (results.some((r) => r === "fail")) return "fail";
  return "pass";
}

// Re-grade a stored reading against the CURRENT reference value (not its
// snapshot). Used by the lookup view to show what flips after an update.
export function regradeReading(reading, currentMin) {
  if (currentMin === null || currentMin === undefined) return null;
  const value = Number(reading.value);
  if (Number.isNaN(value)) return null;
  return value >= Number(currentMin) ? "pass" : "fail";
}
